import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, TextField, Tooltip, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';
import { ContentCopyRounded, DescriptionOutlined, DownloadRounded, LanOutlined, LanguageOutlined, RouterOutlined, ToggleOnOutlined } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import copy from 'copy-to-clipboard';

import { pageSxFor } from '../components/pageLayout';
import useDialogStyles from '../hooks/useDialogStyles';
import { SwitchField } from '../components/dialogKit';
import { EditPageHeader, SectionCard, SegmentedField } from '../components/vpn/formParts';
import { FONT_MONO } from '../config/fonts';

const STATUS_OPTIONS = [
  { label: 'Inactive', value: 0 },
  { label: 'Active', value: 1 },
];

const EditServerIP = () => {
  const { serverIPId } = useParams();
  const location = useLocation();
  const { serverIP } = location.state || {};
  // theme
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;
  const { fieldSx, cancelButtonSx, scrollbarSx } = useDialogStyles();

  const navigate = useNavigate();
  // is large desktop screen
  const isNonMediumScreen = useMediaQuery("(min-width: 1200px)");

  const [ip, setIP] = useState('');
  const [port, setPort] = useState(443);
  const [domainName, setDomainName] = useState('');
  const [useDomain, setUseDomain] = useState(1);
  const [fakeDomainName, setFakeDomainName] = useState('');
  const [useFakeDomain, setUseFakeDomain] = useState(0);
  const [serverIPStatus, setServerIPStatus] = useState(1);
  const [ovpn, setOVPN] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if(serverIP?.ip) {
      setIP(serverIP.ip);
    }
    if(serverIP?.port) {
      setPort(serverIP.port);
    }
    if(serverIP?.domain_name) {
      setDomainName(serverIP?.domain_name);
    }
    if(serverIP?.use_domain) {
      setUseDomain(serverIP?.use_domain);
    }
    if(serverIP?.fake_domain) {
      setFakeDomainName(serverIP?.fake_domain);
    }
    if(serverIP?.use_fakedomain) {
      setUseFakeDomain(serverIP?.use_fakedomain);
    }
    if(serverIP?.status) {
      setServerIPStatus(serverIP?.status);
    }
    if(serverIP?.ovpn) {
      try {
        setOVPN(atob(serverIP?.ovpn));
      } catch {
        setOVPN(serverIP.ovpn);   // not base64: show it as it is
      }
    }
  }, [
    serverIP?.ip,
    serverIP?.port,
    serverIP?.domain_name,
    serverIP?.use_domain,
    serverIP?.fake_domain,
    serverIP?.use_fakedomain,
    serverIP?.status,
    serverIP?.ovpn
  ]);

  const handleBackClick = () => {
    navigate("/vpnserverips");
  };

  const handleCopy = () => {
    copy(ovpn);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleDownload = () => {
    const blob = new Blob([ovpn], { type: 'application/x-openvpn-profile' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${ip || 'server'}.ovpn`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const lineCount = ovpn ? ovpn.split('\n').length : 0;
  const idLabel = String(serverIPId || '').replace(/^:/, '');

  return (
    <Box sx={{ ...pageSxFor(isNonMediumScreen), height: 'auto' }}>
      <EditPageHeader
        icon={RouterOutlined}
        title="Edit Server IP"
        subtitle={`Server IP ID ${idLabel}${ip ? ` · ${ip}` : ''}`}
        onBack={handleBackClick}
      />

      {!serverIP && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No server IP was passed to this page. Open it from the Server IPs list.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1.2fr' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        {/* ---------- left: details ---------- */}
        <Box sx={{ display: 'grid', gap: 2.5, minWidth: 0 }}>
          <SectionCard icon={LanOutlined} title="Address">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 140px' }, gap: 1.5 }}>
              <TextField
                size="small"
                label="Server IP"
                value={ip}
                onChange={(e) => setIP(e.target.value)}
                fullWidth
                autoComplete="off"
                sx={fieldSx}
                InputProps={{ sx: { fontFamily: FONT_MONO } }}
              />
              <TextField
                size="small"
                label="Port"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                sx={fieldSx}
                InputProps={{ sx: { fontFamily: FONT_MONO } }}
              />
            </Box>
          </SectionCard>

          <SectionCard icon={LanguageOutlined} title="Domains">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 220px' }, gap: 1.5, alignItems: 'start' }}>
              <TextField size="small" label="Domain" value={domainName} onChange={(e) => setDomainName(e.target.value)} fullWidth sx={fieldSx} />
              <SwitchField
                label="Use domain"
                checked={Number(useDomain) === 1}
                onChange={(on) => setUseDomain(on ? 1 : 0)}
                onText="Clients use this name"
                offText="Not used"
              />
              <TextField size="small" label="Fake domain" value={fakeDomainName} onChange={(e) => setFakeDomainName(e.target.value)} fullWidth sx={fieldSx} />
              <SwitchField
                label="Use fake domain"
                checked={Number(useFakeDomain) === 1}
                onChange={(on) => setUseFakeDomain(on ? 1 : 0)}
                onText="Clients use this name"
                offText="Not used"
              />
            </Box>
          </SectionCard>

          <SectionCard icon={ToggleOnOutlined} title="Status">
            <SegmentedField label="Server IP status" value={Number(serverIPStatus)} options={STATUS_OPTIONS} onChange={setServerIPStatus} />
          </SectionCard>
        </Box>

        {/* ---------- right: OVPN profile ---------- */}
        <SectionCard
          icon={DescriptionOutlined}
          title="OVPN profile"
          subtitle={ovpn ? `${lineCount} lines` : 'No profile for this IP'}
          action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title={copied ? 'Copied' : 'Copy to clipboard'}>
                <span>
                  <Button size="small" onClick={handleCopy} disabled={!ovpn} startIcon={<ContentCopyRounded />} sx={{ ...cancelButtonSx, textTransform: 'none' }}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </span>
              </Tooltip>
              <Button size="small" onClick={handleDownload} disabled={!ovpn} startIcon={<DownloadRounded />} sx={{ ...cancelButtonSx, textTransform: 'none' }}>
                .ovpn
              </Button>
            </Box>
          }
        >
          {ovpn ? (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                maxHeight: { xs: 420, lg: 'calc(100vh - 260px)' },
                overflow: 'auto',
                borderRadius: 1.5,
                fontFamily: FONT_MONO,
                fontSize: '0.75rem',
                lineHeight: 1.6,
                whiteSpace: 'pre',
                color: theme.palette.text.primary,
                backgroundColor: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.04)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : theme.palette.neutral[200]}`,
                ...scrollbarSx,
              }}
            >
              {ovpn}
            </Box>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
              <DescriptionOutlined sx={{ fontSize: 40, color: alpha(accent, 0.6), mb: 1 }} />
              <Typography sx={{ fontSize: '0.85rem' }}>This server IP has no OVPN profile</Typography>
            </Box>
          )}
        </SectionCard>
      </Box>
    </Box>
  )
}

export default EditServerIP
